import * as THREE from 'three';
import type { BallRenderSnapshot } from '@/entities/Ball';
import { ballPanelMaps, ballPanelMesh } from '@/world/soccerPanels';

/**
 * BallView.ts — the visual ball. GS2 (graphics-realism overhaul): a REAL
 * stitched 32-panel match ball. The texture set comes from the shared
 * spherical-Voronoi painter in `@/world/soccerPanels` — the TRUE truncated
 * icosahedron (12 pentagons + 20 hexagons) with grooved, thread-barred seams,
 * quilted panel bevels and a leather roughness map with micro-scuffs. The
 * maps are colour-NEUTRAL near-white leather; each racer's identity colour
 * arrives via material.color (tinting), so ONE cached 512×256 set serves
 * every ball on track — the panels still read on the white ball because the
 * dark pentagons + seam grooves are baked into the albedo.
 *
 * Replaces the old M35 painter (12 pentagons stamped on an equirect canvas):
 * that one had no hexagon seam network, stretched into "ninja stars" near the
 * poles, and its clearcoat 0.7 rendered as one hot toy-plastic dot. The new
 * material sits at clearcoat 0.5 / leather roughness ≈0.42 (encoded in the
 * roughness map) so highlights read as soft leathery reflections.
 *
 * Public API is unchanged (constructor(color, radius), warp, update) so
 * Race / NetRace / IntroClip need no changes. The roll is still derived from
 * the ball's horizontal velocity.
 *
 * Feel knobs: BUMP_SCALE below; groove/stitch/scuff shaping lives in
 * soccerPanels.ts (BALL_SEAM / STITCH_PITCH / ROUGH_BASE and friends).
 */

/** Seam-groove + stitch relief strength (the globe wears 1.6–2.6 on the same
 *  bump language; the match ball sits lower so race-distance shading stays
 *  clean while macro shots keep the grooves). */
const BUMP_SCALE = 1.35;

/** ONE shared texture set for every ball, built lazily at the first BallView
 *  construction (inside a city load — never mid-race) and cached for the
 *  session. Nothing may dispose these: view teardown removes meshes from the
 *  scene without disposing materials/textures, and `userData.shared` opts out
 *  of any shared-texture sweeps (the scenery disposer convention). */
let texCache: {
  map: THREE.CanvasTexture;
  bump: THREE.CanvasTexture;
  rough: THREE.CanvasTexture;
} | null = null;

function ballTextures(): NonNullable<typeof texCache> {
  if (texCache) return texCache;
  const maps = ballPanelMaps();
  const map = new THREE.CanvasTexture(maps.albedo);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8; // seams stay crisp at grazing race-camera angles
  const bump = new THREE.CanvasTexture(maps.bump);
  bump.anisotropy = 4;
  const rough = new THREE.CanvasTexture(maps.rough);
  rough.anisotropy = 4;
  for (const t of [map, bump, rough]) {
    t.userData.shared = true;
    t.wrapS = THREE.RepeatWrapping; // the per-pixel UV wraps across the date line
  }
  texCache = { map, bump, rough };
  return texCache;
}

/** M158: the raised-panel ball geometry, one per radius (all race balls share
 *  one). Shared like the textures: never disposed. */
const geoCache = new Map<number, THREE.BufferGeometry>();
function ballGeometry(radius: number): THREE.BufferGeometry {
  const hit = geoCache.get(radius);
  if (hit) return hit;
  const { positions, indices } = ballPanelMesh();
  for (let i = 0; i < positions.length; i++) positions[i] *= radius;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  // The maps are read per PIXEL from the surface direction (see the shader
  // patch), so the uv attribute only has to exist.
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((positions.length / 3) * 2), 2));
  g.setIndex(new THREE.BufferAttribute(indices, 1));
  g.computeVertexNormals();
  g.computeBoundingSphere();
  g.userData.shared = true;
  geoCache.set(radius, g);
  return g;
}

/** Samples the equirect panel maps from the surface DIRECTION per pixel — the
 *  exact mapping the painter used, so the painted seams land in the geometric
 *  grooves, with no pole pinch and no date-line seam (Tarini's two-u trick). */
function patchDirectionUv(m: THREE.MeshPhysicalMaterial): void {
  m.onBeforeCompile = (s) => {
    s.vertexShader = s.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vBallDir;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvBallDir = position;');
    s.fragmentShader = s.fragmentShader.replace(
      '#include <uv_pars_fragment>',
      `#include <uv_pars_fragment>
varying vec3 vBallDir;
vec2 ballUV() {
  vec3 d = normalize(vBallDir);
  float u = atan(d.x, d.z) * 0.15915494309 + 0.5;
  float u2 = fract(u + 0.5) - 0.5;
  u = fwidth(u) <= fwidth(u2) + 1e-6 ? u : u2;
  return vec2(u, asin(clamp(d.y, -1.0, 1.0)) * 0.31830988618 + 0.5);
}
#define vMapUv ballUV()
#define vBumpMapUv ballUV()
#define vRoughnessMapUv ballUV()`,
    );
  };
  m.customProgramCacheKey = () => 'ball-dir-uv';
}

export class BallView {
  readonly mesh: THREE.Mesh;
  private radius: number;

  constructor(color = 0xff3b3b, radius = 1.15) {
    this.radius = radius;
    const tex = ballTextures();
    const material = new THREE.MeshPhysicalMaterial({
        // The racer's identity colour TINTS the neutral leather albedo — the
        // one texture set serves all 7–8 balls at zero per-ball cost.
        color,
        map: tex.map,
        bumpMap: tex.bump,
        bumpScale: BUMP_SCALE,
        // Real roughness lives in the map (leather ≈0.42, matte seams/threads,
        // scuff speckle) — the scalar must stay 1 (three multiplies them).
        roughnessMap: tex.rough,
        roughness: 1.0,
        metalness: 0.0,
        // Soft PU-leather sheen, not toy gloss: broad clearcoat, no hot dot.
        clearcoat: 0.5,
        clearcoatRoughness: 0.28,
        envMapIntensity: 1.1,
      });
    patchDirectionUv(material);
    // M158: 32 raised panels with real grooved seams (≈5.9k tris, shared).
    this.mesh = new THREE.Mesh(ballGeometry(radius), material);
    this.mesh.castShadow = true;
  }

  warp(snap: BallRenderSnapshot): void {
    this.mesh.position.set(snap.x, snap.y, snap.z);
  }

  update(snap: BallRenderSnapshot, dt: number): void {
    this.mesh.position.set(snap.x, snap.y, snap.z);

    // Roll: spin about the horizontal axis perpendicular to the travel direction.
    const speed = Math.hypot(snap.vx, snap.vz);
    if (speed > 0.01) {
      const axis = new THREE.Vector3(snap.vz, 0, -snap.vx).normalize();
      this.mesh.rotateOnWorldAxis(axis, (speed * dt) / this.radius);
    }
  }
}
